import React, { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { PageHeader, Loading, fmtDate, GedPrepDisclaimer } from "@/components/common";
import { daysLeft } from "@/components/Billing";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Check, Minus, Plus, Loader2, ShieldCheck, CreditCard, Users, School, ScrollText, Sparkles, RefreshCw } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

const money = (n) => `$${Number(n).toFixed(2)}`;
export const seatPrice = (plan, interval, n) => {
  if (!plan.tiers) return plan.per_seat[interval];
  const t = plan.tiers.find((x) => n >= x.min && (x.max == null || n <= x.max));
  return (t || plan.tiers[0])[interval];
};
const TierTable = ({ plan, interval, seats }) => (
  <div className="mt-3 grid grid-cols-3 gap-2" data-testid="billing-tier-table">
    {plan.tiers.map((t) => {
      const on = seats >= t.min && (t.max == null || seats <= t.max);
      return (
        <div key={t.min} className={`rounded-lg border px-3 py-2 text-center ${on ? "border-[#0F7B7D] bg-[#D7F3F1]" : "border-border bg-white"}`}>
          <div className="text-xs text-muted-foreground">{t.max ? `${t.min}–${t.max}` : `${t.min}+`} students</div>
          <div className="font-display font-semibold">{money(t[interval])}</div>
        </div>
      );
    })}
  </div>
);
const STATUS = {
  trialing: ["Free trial", "bg-[#D7F3F1] text-[#0B5B5B]"],
  active: ["Active", "bg-[#DDF7E7] text-[#14663A]"],
  past_due: ["Payment due", "bg-[#FFE1E1] text-[#9B2C2C]"],
  canceled: ["Canceled", "bg-secondary text-foreground"],
  incomplete: ["Incomplete", "bg-[#FFF1C7] text-[#7A5A00]"],
  unpaid: ["Unpaid", "bg-[#FFE1E1] text-[#9B2C2C]"],
};

const Stepper = ({ value, setValue, min = 1, testid }) => (
  <div className="inline-flex items-center border border-border rounded-xl bg-white" data-testid={testid}>
    <Button type="button" variant="ghost" size="icon" onClick={() => setValue(Math.max(min, value - 1))} aria-label="Fewer" data-testid={`${testid}-minus`}><Minus className="h-4 w-4" /></Button>
    <span className="w-10 text-center font-display font-semibold text-lg" data-testid={`${testid}-value`}>{value}</span>
    <Button type="button" variant="ghost" size="icon" onClick={() => setValue(Math.min(1000, value + 1))} aria-label="More" data-testid={`${testid}-plus`}><Plus className="h-4 w-4" /></Button>
  </div>
);

export default function BillingPage() {
  const { user, billing, refreshBilling } = useAuth();
  const [pricing, setPricing] = useState(null);
  const [interval, setBillInterval] = useState("month");
  const [seats, setSeats] = useState(1);
  const [busy, setBusy] = useState(false);
  const [newSeats, setNewSeats] = useState(null);
  const [invoices, setInvoices] = useState(null);

  useEffect(() => {
    api.get("/billing/pricing").then(({ data }) => setPricing(data));
    refreshBilling();
  }, [refreshBilling]);

  useEffect(() => {
    if (billing?.subscription?.stripe_customer_id) api.get("/billing/invoices").then(({ data }) => setInvoices(data)).catch(() => setInvoices([]));
  }, [billing?.subscription?.stripe_customer_id]);

  useEffect(() => {
    if (billing) {
      setSeats(Math.max(1, billing.seats_used || 1));
      if (billing.subscription?.seats) setNewSeats(billing.subscription.seats);
    }
  }, [billing]);

  if (!pricing || !billing) return <Loading />;
  if (user.role === "admin") {
    return (
      <div><PageHeader eyebrow="Billing" title="Billing" subtitle="School admin accounts don't need a plan. Students in classes you run are covered automatically." /></div>
    );
  }

  const planKey = user.role === "teacher" ? "classroom" : user.role === "school" ? "school" : "family";
  const plan = pricing.plans[planKey];
  const isStudent = user.role === "student";
  const effSeats = isStudent ? 1 : seats;
  const unit = seatPrice(plan, interval, effSeats);
  const total = plan.base[interval] + unit * effSeats;
  const sub = billing.subscription;
  const hasPlan = sub && ["trialing", "active", "past_due"].includes(sub.status);
  const covered = billing.access?.has_access && billing.access.reason !== "own_subscription";

  const start = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/billing/checkout", { plan: planKey, interval, seats: effSeats, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(errMsg(e));
      setBusy(false);
    }
  };

  const act = async (path, body, msg) => {
    setBusy(true);
    try {
      await api.post(path, body);
      await refreshBilling();
      toast.success(msg);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const portal = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/billing/portal");
      window.location.href = data.url;
    } catch (e) {
      toast.error(errMsg(e));
      setBusy(false);
    }
  };
  const subPlan = sub ? pricing.plans[sub.plan] : null;
  const subTotal = sub && subPlan ? subPlan.base[sub.interval] + seatPrice(subPlan, sub.interval, sub.seats) * sub.seats : 0;
  const [label, cls] = STATUS[sub?.status] || [sub?.status, "bg-secondary"];

  return (
    <div className="max-w-[1000px]">
      <PageHeader eyebrow="Billing" title={hasPlan ? "Your plan" : "Choose your plan"} subtitle={hasPlan ? "Manage seats, billing and renewal." : `Every plan starts with a ${pricing.trial_days}-day free trial. Add a card today — you won't be charged until the trial ends, and you can cancel anytime before.`} />

      {billing.comp_access && (
        <div className="mb-6 bg-[#FFF1C7] border border-[#F2B705]/40 rounded-[14px] p-5 flex items-center gap-3" data-testid="billing-free-access">
          <ShieldCheck className="h-6 w-6 text-[#7A5A00]" />
          <div className="text-sm"><span className="font-semibold">You have free access</span>{billing.comp_access.expires_at ? ` until ${fmtDate(billing.comp_access.expires_at)}` : ""}, granted by {billing.comp_access.granted_by_name}.{user.role === "parent" ? " Your linked children are covered too." : user.role === "teacher" ? " Students in your classes are covered too." : ""} Nothing to pay.</div>
        </div>
      )}

      {covered && !hasPlan && !billing.comp_access && (
        <div className="mb-6 bg-[#DDF7E7] border border-[#1F9D55]/30 rounded-[14px] p-5 flex items-center gap-3" data-testid="billing-covered">
          <ShieldCheck className="h-6 w-6 text-[#14663A]" />
          <div className="text-sm"><span className="font-semibold">You're covered</span> by {billing.access.covered_by?.name}'s plan. Nothing to pay.</div>
        </div>
      )}

      {billing.comp_access && !hasPlan ? null : hasPlan ? (
        <div className="bg-white border border-border rounded-[18px] p-6 sm:p-8 fade-up" data-testid="billing-current-plan">
          <div className="flex flex-wrap items-center gap-3">
            <h2 className="text-2xl font-display font-semibold">{subPlan?.name} plan</h2>
            <Badge className={cls} data-testid="billing-status-badge">{label}</Badge>
            {sub.cancel_at_period_end && <Badge variant="outline">Cancels {fmtDate(sub.current_period_end)}</Badge>}
          </div>
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-[#FBFAF4] border border-border rounded-xl p-4"><div className="text-xs text-muted-foreground">{sub.status === "trialing" ? "Trial ends" : "Renews"}</div><div className="font-display text-lg font-semibold mt-1" data-testid="billing-renewal-date">{fmtDate(sub.status === "trialing" ? sub.trial_end : sub.current_period_end)}{sub.status === "trialing" && ` · ${daysLeft(sub.trial_end)} days left`}</div></div>
            <div className="bg-[#FBFAF4] border border-border rounded-xl p-4"><div className="text-xs text-muted-foreground">{subPlan?.seat_label === "child" ? "Children covered" : "Student seats"}</div><div className="font-display text-lg font-semibold mt-1" data-testid="billing-seats">{billing.seats_used} of {sub.seats} used</div></div>
            <div className="bg-[#FBFAF4] border border-border rounded-xl p-4"><div className="text-xs text-muted-foreground">Price after trial</div><div className="font-display text-lg font-semibold mt-1">{money(subTotal)} / {sub.interval}</div></div>
          </div>

          {!isStudent && (
            <div className="mt-6 pt-6 border-t border-border flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="flex-1">
                <div className="font-semibold">Change {subPlan?.seat_label === "child" ? "children" : "student seats"}</div>
                <div className="text-sm text-muted-foreground">{money(seatPrice(subPlan, sub.interval, newSeats || sub.seats))} per {subPlan.seat_label} / {sub.interval}{subPlan.tiers ? " (price drops at 50 and 100 students)" : ""}. Changes are prorated.</div>
              </div>
              <Stepper value={newSeats || sub.seats} setValue={setNewSeats} testid="billing-change-seats" />
              <Button disabled={busy || newSeats === sub.seats} onClick={() => act("/billing/seats", { seats: newSeats }, "Seats updated")} data-testid="billing-update-seats-button">{busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Update seats</Button>
            </div>
          )}

          <div className="mt-6 pt-6 border-t border-border flex flex-wrap gap-3">
            <Button variant="outline" className="bg-white" disabled={busy} onClick={portal} data-testid="billing-update-card-button"><CreditCard className="h-4 w-4 mr-2" />Update card</Button>
            {sub.cancel_at_period_end ? (
              <Button variant="outline" className="bg-white" disabled={busy} onClick={() => act("/billing/resume", {}, "Your plan will continue")} data-testid="billing-resume-button">Keep my plan</Button>
            ) : (
              <AlertDialog>
                <AlertDialogTrigger asChild><Button variant="ghost" className="text-destructive" data-testid="billing-cancel-button">Cancel plan</Button></AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader><AlertDialogTitle>Cancel your plan?</AlertDialogTitle><AlertDialogDescription>You'll keep access until {fmtDate(sub.status === "trialing" ? sub.trial_end : sub.current_period_end)}. {sub.status === "trialing" ? "You won't be charged." : "You won't be charged again."}</AlertDialogDescription></AlertDialogHeader>
                  <AlertDialogFooter><AlertDialogCancel>Keep plan</AlertDialogCancel><AlertDialogAction onClick={() => act("/billing/cancel", {}, "Plan will cancel at the end of the period")} data-testid="billing-cancel-confirm">Cancel plan</AlertDialogAction></AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-7 bg-white border border-border rounded-[18px] p-6 sm:p-8" data-testid="billing-plan-card">
            <div className="flex items-center gap-3">
              <div className="h-11 w-11 rounded-xl bg-[#D7F3F1] flex items-center justify-center">{planKey === "family" ? <Users className="h-5 w-5 text-[#0B6B6B]" /> : <School className="h-5 w-5 text-[#0B6B6B]" />}</div>
              <div><h2 className="text-2xl font-display font-semibold">{plan.name}</h2><p className="text-sm text-muted-foreground">{isStudent ? "Your own plan — one learner." : plan.description}</p></div>
            </div>

            <div className="mt-6 inline-flex bg-secondary rounded-xl p-1" role="tablist" data-testid="billing-interval-toggle">
              {[["month", "Monthly"], ["year", "Yearly · 2 months free"]].map(([v, l]) => (
                <button key={v} onClick={() => setBillInterval(v)} className={`h-10 px-4 rounded-lg text-sm font-medium transition-colors duration-150 ${interval === v ? "bg-white shadow-[var(--shadow-sm)]" : "text-muted-foreground"}`} data-testid={`billing-interval-${v}`}>{l}</button>
              ))}
            </div>

            <div className="mt-6 space-y-3 text-sm">
              {plan.base[interval] > 0 && <div className="flex justify-between"><span>{planKey === "school" ? "School account · unlimited teachers" : "Teacher account"}</span><span className="font-medium">{money(plan.base[interval])} / {interval}</span></div>}
              <div className="flex justify-between items-center gap-3">
                <span>{isStudent ? "1 learner" : `${plan.seat_label === "child" ? "Children" : "Students"} · ${money(plan.per_seat[interval])} each${plan.base[interval] > 0 ? " (discounted)" : ""}`}</span>
                {isStudent ? <span className="font-medium">{money(plan.per_seat[interval])} / {interval}</span> : <Stepper value={seats} setValue={setSeats} testid="billing-seats-stepper" />}
              </div>
              {plan.tiers && <TierTable plan={plan} interval={interval} seats={effSeats} />}
              {planKey === "family" && !isStudent && <div className="flex justify-between text-muted-foreground"><span>Parent account</span><span>Included</span></div>}
              <div className="pt-3 border-t border-border flex justify-between items-end">
                <span className="font-semibold">After your free trial</span>
                <span className="font-display text-3xl font-semibold" data-testid="billing-total">{money(total)}<span className="text-sm text-muted-foreground font-sans"> / {interval}</span></span>
              </div>
              {billing.seats_used > 0 && !isStudent && <p className="text-xs text-muted-foreground">You currently have {billing.seats_used} {plan.seat_label === "child" ? "linked child(ren)" : "student(s) in your classes"}. You can change seats anytime.</p>}
            </div>

            <Button className="mt-8 w-full h-12 text-base" onClick={start} disabled={busy} data-testid="billing-start-trial-button">
              {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CreditCard className="h-4 w-4 mr-2" />}
              {billing.trial_available ? `Start ${pricing.trial_days}-day free trial` : "Subscribe"}
            </Button>
            <p className="mt-3 text-xs text-muted-foreground text-center">{billing.trial_available ? `$0 due today. You'll be charged ${money(total)} on day ${pricing.trial_days + 1} unless you cancel.` : "Your free trial has already been used on this account."} Secure checkout by Stripe.</p>
          </div>

          <div className="lg:col-span-5 bg-[#F5F1E6] border border-border rounded-[18px] p-6 sm:p-8">
            <div className="font-display font-semibold text-lg">Everything included</div>
            <ul className="mt-4 space-y-3 text-sm text-[#123645]">
              {["Full course library, Pre-K to College", "Unlimited AI tutoring with Sage", "Unlimited AI-written lessons & quizzes", planKey === "family" ? "Parent progress dashboard" : "Classes, assignments & gradebook", planKey === "family" ? "Home school classes & assignments" : planKey === "school" ? "Unlimited teachers share one seat pool" : "Your students are covered automatically", "Cancel anytime"].map((t) => (
                <li key={t} className="flex gap-2"><Check className="h-4 w-4 text-[#1F9D55] mt-0.5 shrink-0" />{t}</li>
              ))}
            </ul>
            {isStudent && <p className="mt-6 text-xs text-muted-foreground">Tip: if a parent links you with your code ({billing.link_code}) or your teacher adds you to a paid class, you don't need your own plan.</p>}
          </div>
        </div>
      )}
      {sub?.stripe_customer_id && (
        <div className="mt-8" data-testid="billing-invoices">
          <h3 className="text-sm font-display uppercase tracking-[0.14em] text-muted-foreground mb-3">Invoices</h3>
          <div className="bg-white border border-border rounded-[14px] overflow-x-auto">
            {invoices === null ? <p className="p-5 text-sm text-muted-foreground">Loading invoices...</p> : invoices.length === 0 ? <p className="p-5 text-sm text-muted-foreground">No invoices yet.</p> : (
              <table className="w-full text-sm" data-testid="billing-invoices-table">
                <thead><tr className="text-left text-muted-foreground border-b border-border"><th className="p-3 font-medium">Date</th><th className="p-3 font-medium">Invoice</th><th className="p-3 font-medium">Amount</th><th className="p-3 font-medium">Status</th><th className="p-3" /></tr></thead>
                <tbody>
                  {invoices.map((i) => (
                    <tr key={i.id} className="border-b border-border last:border-0">
                      <td className="p-3">{fmtDate(i.created)}</td>
                      <td className="p-3">{i.number || "—"}</td>
                      <td className="p-3">{money(i.status === "paid" ? i.amount_paid : i.amount_due)}</td>
                      <td className="p-3 capitalize">{i.status}</td>
                      <td className="p-3 text-right whitespace-nowrap">
                        {i.hosted_invoice_url && <a href={i.hosted_invoice_url} target="_blank" rel="noreferrer" className="underline mr-3" data-testid="invoice-view-link">View</a>}
                        {i.invoice_pdf && <a href={i.invoice_pdf} target="_blank" rel="noreferrer" className="underline" data-testid="invoice-pdf-link">PDF</a>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {user.role !== "school" && (
        <div className="mt-10 rounded-[18px] border border-border bg-white p-6 sm:p-7" data-testid="billing-ged-addon">
          <div className="grid lg:grid-cols-[1.3fr_1fr] gap-6 items-start">
            <div>
              <div className="flex items-center gap-3">
                <div className="h-11 w-11 rounded-xl bg-[#FFF1C7] flex items-center justify-center"><ScrollText className="h-5 w-5 text-[#7A5A00]" /></div>
                <div>
                  <div className="text-xs font-display uppercase tracking-[0.14em] text-muted-foreground">Add-on · adult learners</div>
                  <h2 className="font-display font-semibold text-xl">GED Prep</h2>
                </div>
              </div>
              <p className="mt-3 text-sm text-muted-foreground max-w-xl">
                GED Prep is a separate program. One-time payment unlocks 12 months of GED-aligned courses and the AI tutor. Timed practice tests are sold as an optional add-on. No subscription, no free trial.
              </p>
              <GedPrepDisclaimer className="mt-3" />
            </div>
            <div className="rounded-[14px] bg-[#FBFAF4] border border-border p-4">
              <div className="flex items-end gap-2">
                <span className="text-3xl font-display font-semibold" data-testid="billing-ged-base-price">$50</span>
                <span className="text-xs text-muted-foreground pb-1">one-time · 12 months</span>
              </div>
              <div className="mt-3 pt-3 border-t border-border space-y-2 text-xs">
                <div className="flex justify-between items-center"><span className="inline-flex items-center gap-1.5"><Sparkles className="h-3.5 w-3.5 text-[#7A5A00]" />Practice Tests pack</span><span className="font-semibold">+ $20</span></div>
                <div className="flex justify-between items-center"><span className="inline-flex items-center gap-1.5"><RefreshCw className="h-3.5 w-3.5 text-[#0B6B6B]" />Retake any subject</span><span className="font-semibold">+ $5</span></div>
              </div>
              <Button asChild className="mt-4 w-full h-10" data-testid="billing-ged-cta"><Link to="/ged">See GED Prep</Link></Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
